import { Box, useFormControl } from '@mui/material'
import { SliderProps as Mui5SliderProps } from '@mui/material/Slider'
import { useEffect, useMemo, useState } from 'react'
import { Typography } from '../Typography'
import SliderFootNote from './SliderFootnote'
import SliderPlusMinus from './SliderPlusMinus'
import { StyledFormHelperText, StyledMui5Slider, StyledTextField } from './styles'

export type SliderProps = Mui5SliderProps & {
  header?: string
  subHeader?: string
  showNumberInput?: boolean
  numberInputPosition?: 'left' | 'right'
  showNumericals?: boolean
  showPlusMinus?: boolean
  footnote?: string
  footnotePosition?: 'left' | 'center' | 'right'
}

const DEFAULT_MARKS = [
  {
    value: 0,
    label: '0',
  },
  {
    value: 100,
    label: '100',
  },
]

// * MUI Slider API Spec  https://mui.com/material-ui/api/slider/
// For 3.0 version only
export const Slider3 = ({
  header,
  subHeader,
  showNumericals,
  showNumberInput,
  showPlusMinus,
  numberInputPosition = 'right',
  footnote,
  footnotePosition = 'right',
  value,
  disabled = false,
  ...props
}: SliderProps) => {
  const marks = showNumericals ? DEFAULT_MARKS : props?.marks
  const [inputValue, setInputValue] = useState((props?.defaultValue || value) ?? 0)
  const { error } = useFormControl() || {}

  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value)
    }
  }, [value, inputValue])

  const handleSliderChange = (event: Event, newValue: number | number[], activeThumb: number) => {
    setInputValue(newValue)
    props?.onChange?.(event, newValue, activeThumb)
  }

  const onInputChange = (newValue: number) => {
    if (newValue < (props?.min || 0)) return
    if (props?.max && newValue > (props?.max || 100)) return
    setInputValue(newValue)
  }

  const valueIsValidDataType = useMemo(() => {
    if (typeof inputValue === 'number') return true
    if (
      Array.isArray(inputValue) &&
      inputValue.length === 2 &&
      typeof inputValue[0] === 'number' &&
      typeof inputValue[1] === 'number'
    ) {
      return true
    }
    return false
  }, [inputValue])

  return (
    <Box width="100%">
      <div>
        <Typography textVariant="subtitle1">{header}</Typography>
        <Typography textVariant="body2">{subHeader}</Typography>
      </div>
      <Box
        display="flex"
        flexDirection={numberInputPosition === 'right' ? 'row' : 'row-reverse'}
        justifyContent="left"
        gap="12px"
      >
        <StyledMui5Slider
          color={error ? 'error' : 'primary'}
          marks={marks}
          disabled={disabled}
          {...props}
          value={valueIsValidDataType ? inputValue : 0}
          onChange={handleSliderChange}
        />
        <div>
          {showNumberInput && (
            <StyledTextField
              value={inputValue}
              onChange={(event) => onInputChange(Number(event.target.value))}
              type="number"
              disabled={disabled}
              error={error}
            />
          )}
          {showPlusMinus && (
            <SliderPlusMinus value={inputValue as number} onButtonClick={onInputChange} disabled={disabled} />
          )}
        </div>
      </Box>
      <SliderFootNote footnote={footnote} footnotePosition={footnotePosition} disabled={disabled} />
      {error && <StyledFormHelperText error={error}>Incorrect input!</StyledFormHelperText>}
    </Box>
  )
}
